import { getCities, getStates } from '@brazilian-utils/brazilian-utils';
import axios from 'axios';
import { debounce } from 'lodash';

import { listChecklists } from 'api/checklists';
import { listCompanies } from 'api/company';
import { listDrivers } from 'api/drivers';
import { listPartners } from 'api/partners';
import { listProjects } from 'api/projects';
import { listRegionals } from 'api/regionals';
import { listRoles } from 'api/roles';
import { listUsers } from 'api/users';
import { listVehicles, listVehicleTypes } from 'api/vehicles';
import { GeneralStatus } from 'enums/status';
import {
  ActivityTypes,
  ChecklistTypes,
  PartnerTypes,
  ShippingTypes,
} from 'enums/types';

import useRouteStore from '../stores/useRoute';

export const debounceDriver = debounce((value:string, callback: (_:any[]) => void) => {
  listDrivers({
    name: value?.toLocaleLowerCase(),
    status: [
      GeneralStatus.Active,
      GeneralStatus.Invited,
    ],
  }).then(({ data }) => {
    const options = data?.data?.map((item) => ({
      label: item.name,
      id: item.id,
    }));
    callback(options);
  });
}, 500);

export const debounceUser = debounce((value:string, callback: (_:any[]) => void) => {
  listUsers({
    name: value?.toLocaleLowerCase(),
    status: [
      GeneralStatus.Active,
      GeneralStatus.Invited,
    ],
  }).then(({ data }) => {
    const options = data?.data?.map((item) => ({
      label: item.name,
      id: item.id,
    }));
    callback(options);
  });
}, 500);

export const debounceVehicle = debounce((value: string, callback: (_: any[]) => void) => {
  listVehicles({
    licensePlate: value?.toLocaleLowerCase(),
    status: [GeneralStatus.Active],
  }).then(({ data }) => {
    const options = data?.data?.map((item) => ({
      label: item.licensePlate,
      id: item.id,
      company: item.company,
    }));
    callback(options);
  });
}, 500);
export const debounceDriversVehicles = (drivers: any[]) => debounce(
  (value: string, callback: (_: any[]) => void) => {
    listVehicles({
      licensePlate: value?.toLocaleLowerCase(),
      'drivers.name': drivers,
      status: [GeneralStatus.Active],
    }).then(({ data }) => {
      const options = data?.data?.map((item) => ({
        label: item.licensePlate,
        id: item.id,
        company: item.company,
      }));
      callback(options);
    });
  }, 500);

export const debounceVehicleType = debounce((value: string, callback: (_: any[]) => void) => {
  listVehicleTypes({ name: value?.toLocaleLowerCase() }).then(({ data }) => {
    const options = data?.data?.map((item) => ({
      label: item.name,
      id: item.id,
      company: item.company,
    }));
    callback(options);
  });
}, 500);

export const debounceChecklists = debounce((value: string, callback: (_: any[]) => void) => {
  listChecklists({
    name: value?.toLocaleLowerCase(),
    status: [GeneralStatus.Active],
  }).then(({ data }) => {
    const options = data?.data?.map((item) => ({
      label: item.name,
      id: item.id,
      company: item.company,
    }));
    callback(options);
  });
}, 500);

export const debouncePartner = debounce((value:string, callback: (_:any[]) => void) => {
  listPartners({
    name: value?.toLocaleLowerCase(),
    status: [GeneralStatus.Active],
  }).then(({ data }) => {
    const options = data?.data?.map((item) => ({
      label: item.name,
      id: item.id,
      address: item.address,
      company: item.company,
    }));
    callback(options);
  });
}, 500);

export const debounceCompany = debounce((value:string, callback: (_:any[]) => void) => {
  listCompanies({
    name: value?.toLocaleLowerCase(),
    status: [GeneralStatus.Active],
  }).then((res) => {
    const options = res?.data?.data?.map((item) => ({
      label: item.name,
      company: item.company,
      ...item,
    }));
    callback(options);
  });
}, 500);

export const debounceProject = debounce((value:string, callback: (_:any[]) => void) => {
  listProjects({
    name: value?.toLocaleLowerCase(),
    status: GeneralStatus.Active,
  }).then(({ data }) => {
    const options = data?.data?.map((item) => ({
      label: item.name,
      id: item.id,
      company: item.company,
    }));
    callback(options);
  });
}, 500);

export const debounceRegional = debounce((value:string, callback: (_:any[]) => void) => {
  listRegionals({
    name: value,
    status: GeneralStatus.Active,
  }).then(({ data }) => {
    const options = data?.data?.map((item) => ({
      label: item.name,
      id: item.id,
      company: item.company,
    }));
    callback(options);
  });
}, 500);

export const debounceRole = debounce((value:string, callback: (_:any[]) => void) => {
  listRoles({ name: value?.toLocaleLowerCase() }).then(({ data }) => {
    const options = data?.data?.map((item) => ({
      label: item.name,
      id: item.id,
      company: item.company,
    }));
    callback(options);
  });
}, 500);

export const debounceBatch = debounce((value: string, callback: (_: any[]) => void) => {
  const { route } = useRouteStore.getState();
  const activities = route.activities.filter((activity) => activity.type === ActivityTypes.Collect);

  const options = activities?.map((item) => ({ label: `Carga da atividade ${item.order}`, id: item.id }));
  callback(options);
}, 500);

export const debounceState = debounce((value: string, callback: (_: any[]) => void) => {
  const states = getStates().filter((state) => {
    const query = new RegExp(value?.toLocaleLowerCase());
    return state.code.toLocaleLowerCase().match(query)
    || state.name.toLocaleLowerCase().match(query);
  });

  const options = states?.map((state) => ({ label: state.code, name: state.code }));
  callback(options);
}, 500);

export const debounceCity = debounce((value: string, callback: (_: any[]) => void) => {
  const cities = getCities().filter(
    (city) => city.toLowerCase().match(new RegExp(value?.toLocaleLowerCase())));

  const options = cities?.map((city) => ({ label: city, name: city }));
  callback(options);
}, 500);

export const debounceTableFilter = debounce(
  (value: string, callback: (_: any[]) => void, options: any[]) => {
    const filteredOptions = options.filter(
      ({ label }) => label?.toLocaleLowerCase().match(new RegExp(value?.toLowerCase())),
    );
    callback(filteredOptions);
  }, 100);

export const debounceViaCep = debounce(
  (value: string, callback: (_: any[]) => void) => {
    axios.get(`https://viacep.com.br/ws/${value}/json/`)
      .then(({ data }) => callback(data)).catch(() => null);
  }, 200);

export const debouncePartnerTypes = debounce(
  (value: string, callback: (_: any[]) => void) => {
    const types = Object.values(PartnerTypes);
    const filteredOptions = types.filter(
      (type) => type.toLocaleLowerCase().match(new RegExp(value?.toLocaleLowerCase())),
    );

    callback(filteredOptions.map((option) => ({ label: option, value: option })));
  }, 100);

export const debounceChecklistTypes = debounce(
  (value: string, callback: (_: any[]) => void) => {
    const types = Object.values(ChecklistTypes);
    const filteredOptions = types.filter(
      (type) => type.toLocaleLowerCase().match(new RegExp(value?.toLocaleLowerCase())),
    );

    callback(filteredOptions.map((option) => ({ label: option, value: option })));
  }, 100);

export const debounceShipping = debounce(
  (value:string, callback: (_: any[]) => void) => {
    const types = Object.values(ShippingTypes);
    const filteredOptions = types.filter(
      (type) => type.toLocaleLowerCase().match(new RegExp(value?.toLocaleLowerCase())),
    );
    callback(filteredOptions.map((option) => ({ label: option, value: option })));
  }, 100);
